import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth, ROLES_FULL_ACCESS } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import {
  Upload,
  Download,
  FileText,
  Loader2,
  Euro,
  Filter,
} from "lucide-react";

const MONTHS = [
  { value: "01", label: "Janvier" },
  { value: "02", label: "Février" },
  { value: "03", label: "Mars" },
  { value: "04", label: "Avril" },
  { value: "05", label: "Mai" },
  { value: "06", label: "Juin" },
  { value: "07", label: "Juillet" },
  { value: "08", label: "Août" },
  { value: "09", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

const getInitials = (name) =>
  (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

const PayslipsPage = () => {
  const { user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileInputRef = useRef(null);

  // Form state
  const [selectedUser, setSelectedUser] = useState("");
  const [period, setPeriod] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  // Filters
  const [filterUserId, setFilterUserId] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterYear, setFilterYear] = useState("all");

  const isFullAccess = ROLES_FULL_ACCESS.includes(user?.role);

  const usersMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users]
  );

  const yearOptions = useMemo(() => {
    const years = [...new Set(payslips.map(p => p.period.split("-")[0]))].sort((a, b) => b - a);
    return years;
  }, [payslips]);

  const filteredPayslips = useMemo(() => {
    return payslips.filter(p => {
      if (filterUserId !== "all" && p.user_id !== filterUserId) return false;
      const [year, month] = p.period.split("-");
      if (filterYear !== "all" && year !== filterYear) return false;
      if (filterMonth !== "all" && month !== filterMonth) return false;
      return true;
    });
  }, [payslips, filterUserId, filterMonth, filterYear]);

  const hasActiveFilters = filterUserId !== "all" || filterMonth !== "all" || filterYear !== "all";
  const resetFilters = () => { setFilterUserId("all"); setFilterMonth("all"); setFilterYear("all"); };

  useEffect(() => {
    fetchPayslips();
    if (ROLES_FULL_ACCESS.includes(user?.role)) {
      fetchUsers();
    }
  }, []);

  const fetchPayslips = async () => {
    try {
      const response = await axios.get(`${API}/payslips`);
      setPayslips(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des bulletins");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Seuls les fichiers PDF sont acceptés");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser || !period || !selectedFile) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const params = new URLSearchParams();
      params.append("user_id", selectedUser);
      params.append("period", period);
      if (grossAmount) params.append("gross_amount", grossAmount);

      await axios.post(`${API}/payslips/upload?${params.toString()}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Bulletin de paie téléversé !");
      setIsDialogOpen(false);
      resetForm();
      fetchPayslips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du téléversement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (payslipId, filename) => {
    setDownloadingId(payslipId);
    try {
      const response = await axios.get(`${API}/payslips/${payslipId}/download`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Téléchargement réussi !");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloadingId(null);
    }
  };

  const resetForm = () => {
    setSelectedUser("");
    setPeriod("");
    setGrossAmount("");
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatPeriod = (period) => {
    const [year, month] = period.split("-");
    const months = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR"
    }).format(amount);
  };

  const generatePeriodOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      options.push({ value, label: formatPeriod(value) });
    }
    return options;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payslips-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Bulletins de paie
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1">
            {ROLES_FULL_ACCESS.includes(user?.role)
              ? "Gérez et téléversez les bulletins de paie"
              : "Consultez et téléchargez vos bulletins de paie"}
          </p>
        </div>
        
        {ROLES_FULL_ACCESS.includes(user?.role) && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal={true}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] rounded-full"
                data-testid="upload-payslip-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Téléverser un bulletin
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md dark:bg-[#0B1628] dark:border-[#1A3050]">
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
                  Téléverser un bulletin de paie
                </DialogTitle>
                <DialogDescription className="dark:text-[#7EA8CE]">
                  Sélectionnez un collaborateur et téléversez son bulletin.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="user" className="dark:text-[#D6E4F5]">Collaborateur *</Label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger data-testid="payslip-user-select" className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                      <SelectValue placeholder="Sélectionnez un collaborateur" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="period" className="dark:text-[#D6E4F5]">Période *</Label>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger data-testid="payslip-period-select" className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                      <SelectValue placeholder="Sélectionnez une période" />
                    </SelectTrigger>
                    <SelectContent>
                      {generatePeriodOptions().map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="grossAmount" className="dark:text-[#D6E4F5]">Salaire brut (€)</Label>
                  <Input
                    id="grossAmount"
                    type="number"
                    step="0.01"
                    placeholder="3500.00"
                    value={grossAmount}
                    onChange={(e) => setGrossAmount(e.target.value)}
                    className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                    data-testid="payslip-gross"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file" className="dark:text-[#D6E4F5]">Fichier PDF *</Label>
                  <div className="border-2 border-dashed border-gray-200 dark:border-[#1A3050] rounded-xl p-4 text-center hover:border-[#1A4D2E]/50 dark:hover:border-[#4B8BF4]/50 transition-colors">
                    <input
                      ref={fileInputRef}
                      id="file"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="payslip-file-input"
                    />
                    <label
                      htmlFor="file"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <FileText className="w-8 h-8 text-[#9CA3AF]" />
                      {selectedFile ? (
                        <span className="text-sm font-medium text-[#1A4D2E] dark:text-[#4CAF70]">
                          {selectedFile.name}
                        </span>
                      ) : (
                        <span className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">
                          Cliquez pour sélectionner un fichier PDF
                        </span>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d]"
                    disabled={isSubmitting}
                    data-testid="submit-payslip"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Téléverser"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="card-neumorphic">
        <CardContent className="p-4">
          <div className={`grid grid-cols-1 gap-3 ${isFullAccess ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            {isFullAccess && (
              <select
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-white dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5] px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="all">Tous les collaborateurs</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                ))}
              </select>
            )}
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-white dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5] px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="all">Tous les mois</option>
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-white dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5] px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="all">Toutes les années</option>
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E]">
                {filteredPayslips.length} bulletin{filteredPayslips.length !== 1 ? "s" : ""} affiché{filteredPayslips.length !== 1 ? "s" : ""}
              </p>
              <button
                onClick={resetFilters}
                className="text-xs text-[#4B5563] dark:text-[#7EA8CE] hover:text-[#1A4D2E] dark:hover:text-[#4B8BF4] transition-colors"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payslips Table */}
      <Card className="card-neumorphic">
        <CardContent className="p-0">
          {filteredPayslips.length === 0 ? (
            <div className="text-center py-12 text-[#9CA3AF] dark:text-[#3D607E]">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{payslips.length === 0 ? "Aucun bulletin de paie disponible" : "Aucun bulletin ne correspond aux filtres"}</p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="sm:hidden divide-y divide-gray-100 dark:divide-[#1A3050]">
                {filteredPayslips.map((payslip) => (
                  <div key={payslip.id} className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {(() => {
                        const u = usersMap[payslip.user_id] || user;
                        return (
                          <Avatar className="w-9 h-9 shrink-0">
                            <AvatarImage src={u?.avatar_url} />
                            <AvatarFallback className="text-xs bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70] font-semibold">
                              {getInitials(payslip.user_name)}
                            </AvatarFallback>
                          </Avatar>
                        );
                      })()}
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-[#111827] dark:text-[#D6E4F5] truncate">
                          <ClickableUser userId={payslip.user_id}>{payslip.user_name}</ClickableUser>
                        </p>
                        <p className="text-xs text-[#6B7280] dark:text-[#7EA8CE]">
                          {formatPeriod(payslip.period)}
                        </p>
                        {payslip.gross_amount && (
                          <p className="text-xs text-[#9CA3AF] dark:text-[#7EA8CE] mt-0.5">
                            Brut {formatCurrency(payslip.gross_amount)}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-[#1A4D2E] hover:text-[#143D24] hover:bg-[#E8F5E9] dark:text-[#4CAF70] dark:hover:bg-[#1A4D2E]/20 shrink-0"
                      onClick={() => handleDownload(payslip.id, payslip.filename)}
                      disabled={downloadingId === payslip.id}
                      data-testid={`download-${payslip.id}`}
                    >
                      {downloadingId === payslip.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Collaborateur</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Fichier</TableHead>
                      <TableHead className="text-right">Brut</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayslips.map((payslip) => (
                      <TableRow key={payslip.id} className="group">
                        {(() => {
                          const u = usersMap[payslip.user_id] || user;
                          return (
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="w-7 h-7 shrink-0">
                                  <AvatarImage src={u?.avatar_url} />
                                  <AvatarFallback className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70] font-semibold">
                                    {getInitials(payslip.user_name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium dark:text-[#D6E4F5] whitespace-nowrap">
                                  <ClickableUser userId={payslip.user_id}>{payslip.user_name}</ClickableUser>
                                </span>
                              </div>
                            </TableCell>
                          );
                        })()}
                        <TableCell>
                          <span className="font-medium text-[#111827] dark:text-[#D6E4F5]">
                            {formatPeriod(payslip.period)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-[#4B5563] dark:text-[#7EA8CE]">
                            <FileText className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
                            <span className="text-sm truncate max-w-[160px]">{payslip.filename}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right dark:text-[#7EA8CE]">
                          {formatCurrency(payslip.gross_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#1A4D2E] hover:text-[#143D24] hover:bg-[#E8F5E9] dark:text-[#4CAF70] dark:hover:bg-[#1A4D2E]/20"
                            onClick={() => handleDownload(payslip.id, payslip.filename)}
                            disabled={downloadingId === payslip.id}
                            data-testid={`download-${payslip.id}`}
                          >
                            {downloadingId === payslip.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayslipsPage;
